import { datadogRum, Site, type RumEvent } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

let isInitialized = false;
let initializationAttempted = false;
let isReplayForced = false;
const NEXT_DATA_PREFIX = '/_next/data/';
const SESSION_REPLAY_SAMPLE_RATE = Number(
  process.env.NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE ?? '10',
);
const isSessionReplayEnabled = SESSION_REPLAY_SAMPLE_RATE > 0;

/**
 * Patterns for RUM errors that are not actionable — browser extensions,
 * ad-blockers, autoplay policy, third-party scripts, and network noise.
 * Dropping these keeps the Error Tracking dashboard focused on real bugs.
 */
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  // Ad-blockers preventing third-party scripts from loading
  /Failed to load.*googletagmanager/,
  /fbq is not defined/,
  /Can't find variable: fbq/,

  // Browser extensions
  /runtime\.sendMessage/,
  /Can't find variable: _AutofillCallbackHandler/,
  /PerformanceMonitoringJavascriptInterface/,
  /Java object is gone/,

  // Video autoplay policy (browser-enforced, not a bug)
  /play\(\) failed because the user didn't interact/,
  /play\(\) request was interrupted/,
  /play\(\) can only be initiated by a user gesture/,
  /The play method is not allowed/,
  // WebKit wording when Low Power Mode or an auto-play setting blocks playback
  /The request is not allowed by the user agent or the platform/,

  // Third-party: specific noisy Savvy product customization widget error
  /o\.getFlow is not a function/,

  // Cross-origin opaque errors (no useful info)
  /^Script error\.?$/,

  // CSP violations from browser extensions (real CSP never uses "script-src 'none'")
  /blocked by .* directive of the policy "script-src 'none'/,
];

/**
 * Ignore stale Next.js page-data fetches from a previous deployment.
 * These request the old build ID, 404, and then Next.js hard reloads.
 */
export function isStaleNextData404(event: RumEvent): boolean {
  const resource =
    event.type === 'resource'
      ? event.resource
      : event.type === 'error'
        ? event.error.resource
        : undefined;
  const currentBuildId =
    typeof window === 'undefined'
      ? undefined
      : (window as any).__NEXT_DATA__?.buildId;

  if (resource?.status_code !== 404 || !resource.url || !currentBuildId) {
    return false;
  }

  try {
    const { pathname } = new URL(resource.url, window.location.origin);
    if (!pathname.startsWith(NEXT_DATA_PREFIX)) {
      return false;
    }

    const buildIdInUrl = pathname.slice(NEXT_DATA_PREFIX.length).split('/')[0];

    return buildIdInUrl !== '' && buildIdInUrl !== currentBuildId;
  } catch {
    return false;
  }
}

// We don't want to track any quiz answers in Datadog
const QUIZ_PAGE = /^\/(find-your-routine|surveys\/|onboarding)/;
const QUIZ_EVENT = /^(Quiz|Survey) /;

export function isQuizAction(event: RumEvent): boolean {
  if (event.type !== 'action') return false;
  if (QUIZ_EVENT.test(event.action.target?.name ?? '')) return true;
  try {
    return QUIZ_PAGE.test(new URL(event.view.url).pathname);
  } catch {
    return false;
  }
}

/**
 * Check if user has consented to analytics/performance tracking
 * C0002 is the Performance/Analytics category in OneTrust
 */
function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;

  const activeGroups = (window as any).OnetrustActiveGroups || '';
  const consentedCategories = activeGroups.split(',').filter(Boolean);

  return consentedCategories.includes('C0002');
}

/**
 * Check if OneTrust is available and has consent data
 */
function isOneTrustReady(): boolean {
  if (typeof window === 'undefined') return false;
  // OneTrust sets OnetrustActiveGroups when consent is determined
  return (
    typeof (window as any).OneTrust !== 'undefined' &&
    typeof (window as any).OnetrustActiveGroups === 'string'
  );
}

/**
 * Initialize Datadog RUM with consent-aware tracking
 * Waits for OneTrust to be ready before initializing to ensure proper consent state
 */
export function initializeDatadogRUM(): void {
  if (typeof window === 'undefined') return;
  if (initializationAttempted) return;
  if (
    !process.env.NEXT_PUBLIC_DD_APPLICATION_ID ||
    !process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN
  ) {
    return;
  }

  initializationAttempted = true;

  // Wait for OneTrust to be ready before initializing
  const initWhenReady = () => {
    const checkInterval = setInterval(() => {
      if (isOneTrustReady()) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        performInitialization();
      }
    }, 100);

    // Timeout after 5 seconds - initialize with not-granted if OneTrust doesn't load
    const timeoutId = setTimeout(() => {
      clearInterval(checkInterval);
      if (!isInitialized) {
        performInitialization(true); // Force not-granted
      }
    }, 5000);
  };

  initWhenReady();
}

/**
 * Perform the actual Datadog initialization
 */
function performInitialization(forceNotGranted = false): void {
  if (isInitialized || (window as any).__DD_INIT_DONE) return;

  try {
    // Determine initial consent state
    const hasConsent = forceNotGranted ? false : hasAnalyticsConsent();

    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DD_APPLICATION_ID!,
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN!,
      site: (process.env.NEXT_PUBLIC_DD_SITE || 'datadoghq.com') as Site,
      service: process.env.NEXT_PUBLIC_DD_SERVICE || 'my-seed-live',
      env: process.env.NEXT_PUBLIC_DD_ENV || 'local',
      version: process.env.NEXT_PUBLIC_DD_VERSION || 'unknown',
      sessionSampleRate: Number(
        process.env.NEXT_PUBLIC_DD_SESSION_SAMPLE_RATE ?? '10',
      ),
      sessionReplaySampleRate: SESSION_REPLAY_SAMPLE_RATE,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      // Start with consent state based on OneTrust
      trackingConsent: hasConsent ? 'granted' : 'not-granted',
      beforeSend: (event) => {
        if (isQuizAction(event)) return false;
        if (
          event.type === 'error' &&
          IGNORED_ERROR_PATTERNS.some((pattern) =>
            pattern.test(event.error.message || ''),
          )
        ) {
          return false;
        }

        return !isStaleNextData404(event);
      },
      enableExperimentalFeatures: ['feature_flags'],
      allowedTracingUrls: [
        // Allow seed.com and sd-staging.com subdomains we control.
        // Exclude shop.* (Shopify Storefront API rejects Datadog trace
        // headers in CORS preflight) and checkout.* domains.
        (url: string) => {
          if (/:\/\/(?:shop|checkout)\.(seed\.com|sd-staging\.com)/.test(url)) {
            return false;
          }
          const allowedDomains = [
            '://seed.com',
            '://www.seed.com',
            '://my.seed.com',
            '://api.seed.com',
          ];
          if (allowedDomains.some((domain) => url.includes(domain))) {
            return true;
          }
          return /^https?:\/\/(.*\.)?sd-staging\.com/.test(url);
        },
      ],
    });

    // Tag all RUM events with the Shopify/Recurly variant bucket
    datadogRum.setGlobalContextProperty(
      'seed_variant',
      'shopify',
    );

    // Set up listener for future consent changes
    setupOneTrustListener();

    (window as any).__DD_INIT_DONE = true;
    isInitialized = true;
    applyForcedReplay();
  } catch (error) {
    console.error('Failed to initialize Datadog:', error);
    isInitialized = true; // Prevent retries
  }
}

/**
 * Track custom user actions
 */
export function trackCustomAction(
  name: string,
  context?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || !isInitialized) return;

  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    trackCustomWarning('Failed to track action', { error, name, context });
  }
}

/**
 * Track custom errors
 */
export function trackCustomError(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || !isInitialized) return;

  try {
    datadogRum.addError(error, context);
  } catch (err) {
    trackCustomWarning('Failed to track error', {
      error: err,
      originalError: error,
      context,
    });
  }
}

/**
 * Identify the RUM user by Seed user ID only. Never add PII here.
 */
export function setDatadogUser(userId: string): void {
  if (typeof window === 'undefined' || !isInitialized) return;

  try {
    datadogRum.setUser({ id: userId });
  } catch (error) {
    trackCustomWarning('Failed to set user', { error, userId });
  }
}

/**
 * Update tracking consent dynamically
 */
export function updateTrackingConsent(): void {
  if (!isInitialized) return;

  try {
    const consent = hasAnalyticsConsent() ? 'granted' : 'not-granted';
    datadogRum.setTrackingConsent(consent);
    if (consent === 'granted') applyForcedReplay();
  } catch (error) {
    trackCustomWarning('Failed to update tracking consent', { error });
  }
}

/**
 * Record Session Replay for the rest of this session, bypassing
 * sessionReplaySampleRate. A sample rate of 0 disables this too.
 */
export function forceSessionReplay(): void {
  if (typeof window === 'undefined') return;
  isReplayForced = true;
  applyForcedReplay();
}

// The SDK ignores `force` until RUM has started, so this re-runs after init and consent grants.
function applyForcedReplay(): void {
  if (!isInitialized || !isReplayForced || !isSessionReplayEnabled) return;

  try {
    datadogRum.startSessionReplayRecording({ force: true });
  } catch (error) {
    trackCustomWarning('Failed to force session replay', { error });
  }
}

/**
 * Set up OneTrust consent change listener
 */
function setupOneTrustListener(): void {
  if (typeof window === 'undefined') return;

  const checkInterval = setInterval(() => {
    if (typeof (window as any).OneTrust?.OnConsentChanged === 'function') {
      clearInterval(checkInterval);

      // Immediately sync current consent
      updateTrackingConsent();

      // Listen for consent changes
      (window as any).OneTrust.OnConsentChanged(() => {
        updateTrackingConsent();
      });
    }
  }, 500);

  // Stop checking after 10 seconds
  setTimeout(() => {
    clearInterval(checkInterval);
  }, 10000);
}

/**
 * Check if Datadog is initialized
 */
export function isDatadogInitialized(): boolean {
  return isInitialized;
}

/**
 * Track custom warnings
 */
export function trackCustomWarning(
  message: string,
  context?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || !isInitialized) return;

  try {
    datadogLogs.logger.warn(message, context);
  } catch {
    // Silent fail
  }
}

/**
 * Track feature flag evaluation
 */
export function trackFeatureFlag(flagName: string, flagValue: string): void {
  if (typeof window === 'undefined' || !isInitialized) return;

  try {
    // Sanitize flag name based on Datadog requirements
    const sanitizedName = flagName.replace(
      /[.:+=&|><!()\[\]{}^""\"~*?\\]/g,
      '_',
    );
    datadogRum.addFeatureFlagEvaluation(sanitizedName, flagValue);
  } catch (error) {
    trackCustomWarning('Failed to track feature flag', {
      error,
      flagName,
      flagValue,
    });
  }
}
